import type { HomeCard, HomeCardTarget } from '@loot/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { setFocusEntity, useFocusEntity } from '../focus.ts';
import { Badge, Button, ErrorBox, Loading, PageHeader } from './ui.tsx';

/**
 * Cartões da Visão geral de uma frente (ADR 0097). A API conta; aqui fica como cada número
 * aparece e para qual lista ele leva. Textos são chaves de tradução.
 */
export type HomeCardLink = HomeCardTarget;

export interface HomeCardDefinition {
  /** Igual ao id que a API devolve. */
  id: string;
  /** Prazos ficam sempre à vista; pendências só aparecem quando existem. */
  section: 'deadline' | 'pending';
  title: string;
  criterion: string;
  /** Unidade do número, com plural pelo `count`. Prazo com par de números não usa. */
  unit?: string;
  link: HomeCardLink;
  /** Prazo com par de números: para onde vai cada um. */
  outsideLink?: HomeCardLink;
  noneLink?: HomeCardLink;
  /** Etapa normal do fluxo fica sem destaque: só o que trava a operação chama atenção. */
  attention?: boolean;
  /** Prazo que ainda vai vencer (ex.: renovações nos próximos 60 dias): o número não fica vermelho. */
  upcoming?: boolean;
  /** Prefixo de tradução dos itens de `detail`, na ordem de `detailOrder`. */
  detailPrefix?: string;
  detailOrder?: string[];
}

export interface HomeCardsConfig {
  queryKey: string;
  endpoint: string;
  /** Tela Consultar pendências da frente. */
  consultTo: string;
  cards: HomeCardDefinition[];
}

export function IndicatorCard({
  children,
  tone = 'plain',
}: {
  children: ReactNode;
  tone?: 'plain' | 'attention';
}) {
  return (
    <div
      className={
        tone === 'attention'
          ? 'flex h-full flex-col gap-1 rounded-lg border border-rule border-l-4 border-l-warn bg-sheet p-4 shadow-sm'
          : 'flex h-full flex-col gap-1 rounded-lg border border-rule bg-sheet p-4 shadow-sm'
      }
    >
      {children}
    </div>
  );
}

export function IndicatorFailure({ title, onRetry }: { title: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <IndicatorCard>
      <span className="text-sm font-medium">{title}</span>
      <span role="alert" className="text-sm text-danger">
        {t('homeCards.failed')}
      </span>
      <div>
        <Button variant="secondary" onClick={onRetry}>
          {t('homeCards.retry')}
        </Button>
      </div>
    </IndicatorCard>
  );
}

/**
 * Selo do cartão que conta o grupo todo, mesmo com uma empresa em foco (ADR 0100). Sem foco, não
 * há o que avisar.
 */
export function GroupWideBadge() {
  const { t } = useTranslation();
  const focus = useFocusEntity();
  if (!focus) return null;
  return (
    <span title={t('homeCards.groupWideHint')}>
      <Badge tone="info">{t('homeCards.groupWide')}</Badge>
    </span>
  );
}

/** A lista de um cartão do grupo todo é do grupo todo: ao abrir, a tela sai da empresa em foco. */
export function leaveFocus() {
  setFocusEntity(null);
}

export function PendingIndicator({
  title,
  value,
  unit,
  criterion,
  to,
  attention = false,
  detail,
  groupWide = false,
}: {
  title: string;
  value: number;
  unit: string;
  criterion: string;
  to: ReactNode;
  attention?: boolean;
  detail?: ReactNode;
  groupWide?: boolean;
}) {
  const { i18n } = useTranslation();
  return (
    <IndicatorCard tone={attention && value > 0 ? 'attention' : 'plain'}>
      <span className="text-2xl font-semibold tabular-nums">
        {value.toLocaleString(i18n.language)}
      </span>
      <span className="text-sm font-medium">{title}</span>
      {groupWide && (
        <span>
          <GroupWideBadge />
        </span>
      )}
      <span className="text-xs text-muted">{unit}</span>
      {detail}
      <span className="mt-auto border-t border-dashed border-rule pt-1.5 text-xs text-muted">
        {criterion}
      </span>
      {to}
    </IndicatorCard>
  );
}

const textLink = 'text-sm text-brand-text hover:underline';

function CardLink({
  link,
  className,
  children,
  onClick,
}: {
  link: HomeCardLink;
  className: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <Link
      to={link.to}
      search={(link.search ?? {}) as never}
      className={className}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function CountTile({
  link,
  value,
  label,
  late = false,
}: {
  link: HomeCardLink;
  value: number;
  label: string;
  late?: boolean;
}) {
  const { i18n } = useTranslation();
  return (
    <CardLink
      link={link}
      className="flex min-w-0 flex-1 flex-col rounded-md border border-rule px-3 py-2 hover:border-control-border hover:bg-paper"
    >
      <span
        className={`text-2xl font-semibold tabular-nums ${late && value > 0 ? 'text-danger' : ''}`}
      >
        {value.toLocaleString(i18n.language)}
      </span>
      <span className="text-xs text-muted">{label}</span>
    </CardLink>
  );
}

/** Prazo sem nada vencido: continua à vista, mas sem disputar espaço com o que pede ação. */
export function isIdleDeadline(card: HomeCard): boolean {
  if (card.state !== 'ok') return false;
  if ('count' in card) return card.count === 0;
  return card.inside === 0 && card.outside === 0 && card.none === 0;
}

function IdleDeadline({ def, card }: { def: HomeCardDefinition; card: HomeCard }) {
  const { t } = useTranslation();
  const criterion = t(def.criterion);
  const quiet = 'text-muted hover:text-ink hover:underline';
  return (
    <IndicatorCard>
      <span className="text-sm font-medium">{t(def.title)}</span>
      {'count' in card ? (
        <CardLink link={def.link} className={`text-xs ${quiet}`}>
          0 {t(def.unit ?? 'homeCards.overdue', { count: 0 })}
        </CardLink>
      ) : (
        <span className="flex flex-wrap gap-x-3 text-xs">
          <CardLink link={def.link} className={quiet}>
            0 {t('homeCards.inside')}
          </CardLink>
          <CardLink link={def.outsideLink ?? def.link} className={quiet}>
            0 {t('homeCards.outside')}
          </CardLink>
        </span>
      )}
      <span title={criterion} className="truncate text-xs text-muted">
        {criterion}
      </span>
    </IndicatorCard>
  );
}

function DeadlineIndicator({ def, card }: { def: HomeCardDefinition; card: HomeCard }) {
  const { t } = useTranslation();
  if (isIdleDeadline(card)) return <IdleDeadline def={def} card={card} />;
  return (
    <IndicatorCard>
      <span className="text-sm font-medium">{t(def.title)}</span>
      {card.state === 'rule_inactive' && <Badge>{t('homeCards.ruleInactive')}</Badge>}
      {card.state === 'ok' && 'inside' in card && (
        <div className="flex gap-2">
          <CountTile link={def.link} value={card.inside} label={t('homeCards.inside')} />
          <CountTile
            link={def.outsideLink ?? def.link}
            value={card.outside}
            label={t('homeCards.outside')}
            late
          />
        </div>
      )}
      {card.state === 'ok' && 'inside' in card && card.none > 0 && def.noneLink && (
        <CardLink link={def.noneLink} className="text-xs text-brand-text hover:underline">
          {t('homeCards.noDeadline', { count: card.none })}
        </CardLink>
      )}
      {card.state === 'ok' && 'count' in card && (
        <div className="flex gap-2">
          <CountTile
            link={def.link}
            value={card.count}
            label={t(def.unit ?? 'homeCards.overdue', { count: card.count })}
            late={!def.upcoming}
          />
        </div>
      )}
      <span className="mt-auto border-t border-dashed border-rule pt-1.5 text-xs text-muted">
        {t(def.criterion)}
      </span>
    </IndicatorCard>
  );
}

function PendingFromDefinition({ def, card }: { def: HomeCardDefinition; card: HomeCard }) {
  const { t } = useTranslation();
  if (card.state !== 'ok' || !('count' in card)) return null;
  const { detail, link } = card;
  const prefix = def.detailPrefix;
  return (
    <PendingIndicator
      title={t(def.title)}
      value={card.count}
      unit={def.unit ? t(def.unit, { count: card.count }) : ''}
      criterion={t(def.criterion)}
      attention={def.attention}
      groupWide={card.groupWide}
      detail={
        detail && prefix ? (
          <span className="flex flex-wrap gap-1">
            {(def.detailOrder ?? Object.keys(detail))
              .filter((key) => detail[key])
              .map((key) => (
                <Badge key={key}>
                  {t(`${prefix}.${key}`)}: {detail[key]}
                </Badge>
              ))}
          </span>
        ) : undefined
      }
      to={
        <CardLink
          link={link ?? def.link}
          className={textLink}
          onClick={card.groupWide ? leaveFocus : undefined}
        >
          {t('homeCards.open')}
        </CardLink>
      }
    />
  );
}

/** Nome da frente no Dashboard executivo: só aparece junto de algum cartão dela. */
export function FrontTitle({ title }: { title?: string }) {
  if (!title) return null;
  return <h2 className="border-t border-rule pt-4 text-lg font-semibold">{title}</h2>;
}

/**
 * O que aparece, na ordem definida: cartão que a API não mandou fica de fora, pois a pessoa não
 * abre aquela lista. Pendência zerada só aparece na tela Consultar pendências (`all`).
 */
export function homeCardsView(definitions: HomeCardDefinition[], cards: HomeCard[], all: boolean) {
  const shown = definitions.flatMap((def) => {
    const card = cards.find((c) => c.id === def.id);
    return card ? [{ def, card }] : [];
  });
  const pending = shown.filter((x) => x.def.section === 'pending');
  return {
    deadlines: shown.filter((x) => x.def.section === 'deadline'),
    pending: pending.filter(
      ({ card }) => all || card.state !== 'ok' || !('count' in card) || card.count > 0,
    ),
    hasPendingCards: pending.length > 0,
  };
}

function Blocks({ config, all, title }: { config: HomeCardsConfig; all: boolean; title?: string }) {
  const { t } = useTranslation();
  const Heading = title ? 'h3' : 'h2';
  const hasCards = config.cards.length > 0;
  // Sempre refeito ao abrir: depois de uma correção, o número volta atualizado.
  const query = useQuery({
    queryKey: [config.queryKey, 'home-cards'],
    queryFn: () => api.get<{ cards: HomeCard[] }>(config.endpoint),
    refetchOnMount: 'always',
    enabled: hasCards,
  });
  const retry = () => void query.refetch();
  if (!hasCards) return null;
  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <>
        <FrontTitle title={title} />
        <div className="flex flex-col gap-2">
          <ErrorBox error={query.error} />
          <div>
            <Button variant="secondary" onClick={retry}>
              {t('homeCards.retry')}
            </Button>
          </div>
        </div>
      </>
    );
  }
  const { deadlines, pending, hasPendingCards } = homeCardsView(
    config.cards,
    query.data.cards,
    all,
  );
  if (deadlines.length === 0 && !hasPendingCards) return null;
  return (
    <>
      <FrontTitle title={title} />
      {deadlines.length > 0 && (
        <section className="flex flex-col gap-2" aria-label={t('homeCards.deadlines')}>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <Heading className="text-base font-semibold">{t('homeCards.deadlines')}</Heading>
            <p className="text-sm text-muted">{t('homeCards.deadlinesHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {deadlines.map(({ def, card }) =>
              card.state === 'error' ? (
                <IndicatorFailure key={def.id} title={t(def.title)} onRetry={retry} />
              ) : (
                <DeadlineIndicator key={def.id} def={def} card={card} />
              ),
            )}
          </div>
        </section>
      )}
      {hasPendingCards && (
        <section className="flex flex-col gap-2" aria-label={t('homeCards.pending')}>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <Heading className="text-base font-semibold">{t('homeCards.pending')}</Heading>
            {!all && (
              <Link to={config.consultTo} className={textLink}>
                {t('homeCards.consult')}
              </Link>
            )}
          </div>
          {pending.length === 0 ? (
            <p className="text-sm text-ok">{t('homeCards.nothingPending')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {pending.map(({ def, card }) =>
                card.state === 'error' ? (
                  <IndicatorFailure key={def.id} title={t(def.title)} onRetry={retry} />
                ) : (
                  <PendingFromDefinition key={def.id} def={def} card={card} />
                ),
              )}
            </div>
          )}
        </section>
      )}
    </>
  );
}

/** Faixa de cartões da Visão geral, acima dos atalhos. Com `title`, leva o nome da frente. */
export function HomeCards({ config, title }: { config: HomeCardsConfig; title?: string }) {
  return <Blocks config={config} all={false} title={title} />;
}

/** Consultar pendências: sempre disponível e com todos os cartões, mesmo os zerados. */
export function HomeCardsPage({
  config,
  title,
  description,
}: {
  config: HomeCardsConfig;
  title: string;
  description: string;
}) {
  return (
    <>
      <PageHeader title={title} description={description} />
      <Blocks config={config} all />
    </>
  );
}
